import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { companyService } from "@/services/companyService";
import { CompanyRequest } from "@/types/api";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errorHandler";

export const useCompanies = () => {
  const queryClient = useQueryClient();

  // Query para listar empresas
  const companiesQuery = useQuery({
    queryKey: ["companies"],
    queryFn: () => companyService.getCompanies(),
  });

  // Mutation para criar empresa
  const createCompanyMutation = useMutation({
    mutationFn: (companyData: CompanyRequest) =>
      companyService.createCompany(companyData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success("Empresa criada com sucesso!");
    },
    onError: (error: any) => {
      const message = extractErrorMessage(error, "Erro ao criar empresa");
      toast.error(message);
    },
  });

  // Mutation para atualizar empresa
  const updateCompanyMutation = useMutation({
    mutationFn: ({
      id,
      companyData,
    }: {
      id: string;
      companyData: CompanyRequest;
    }) => companyService.updateCompany(id, companyData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success("Empresa atualizada com sucesso!");
    },
    onError: (error: any) => {
      const message = extractErrorMessage(error, "Erro ao atualizar empresa");
      toast.error(message);
    },
  });

  // Mutation para deletar empresa
  const deleteCompanyMutation = useMutation({
    mutationFn: (id: string) => companyService.deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success("Empresa deletada com sucesso!");
    },
    onError: (error: any) => {
      const message = extractErrorMessage(error, "Erro ao deletar empresa");
      toast.error(message);
    },
  });

  return {
    companies: companiesQuery.data || [],
    isLoading: companiesQuery.isLoading,
    error: companiesQuery.error,
    createCompany: createCompanyMutation.mutate,
    updateCompany: updateCompanyMutation.mutate,
    deleteCompany: deleteCompanyMutation.mutate,
    isCreating: createCompanyMutation.isPending,
    isUpdating: updateCompanyMutation.isPending,
    isDeleting: deleteCompanyMutation.isPending,
  };
};

// Hook para buscar uma empresa específica
export const useCompany = (id: string) => {
  return useQuery({
    queryKey: ["companies", id],
    queryFn: () => companyService.getCompanyById(id),
    enabled: !!id,
  });
};

// Alias para compatibilidade
export const useAllCompanies = useCompanies;
